import { TrendingUp, Megaphone } from "lucide-react";
import type {
  Cohort,
  EmergingSegment,
  MerchantSignalRow,
} from "@/application/signals/types";
import { SignalsCard, EmptyState } from "./SignalsCard";
import { formatCount, formatPercent } from "../format";

/**
 * Segments growing faster than the base and converting better than the median.
 * The build gates these on a volume floor, so a segment cannot qualify on a
 * jump from one signup to three.
 */
export function EmergingSegmentsCard({
  segments,
  onDrill,
}: {
  segments: EmergingSegment[];
  onDrill: (cohort: Cohort) => void;
}) {
  return (
    <SignalsCard
      title="Emerging segments"
      subtitle="Growing and converting above the median"
    >
      {segments.length === 0 ? (
        <EmptyState message="No segment clears the growth and volume floor yet" />
      ) : (
        <ul className="divide-border divide-y">
          {segments.map((segment) => (
            <li key={segment.segment}>
              <button
                type="button"
                className="hover:bg-muted flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left"
                onClick={() =>
                  onDrill({
                    label: segment.label,
                    filter: { segment: segment.segment },
                  })
                }
              >
                <TrendingUp className="h-4 w-4 shrink-0 text-emerald-500" />
                <div className="min-w-0 flex-1">
                  <p className="text-foreground truncate text-sm">
                    {segment.label}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {formatCount(segment.recentSignups)} recent vs{" "}
                    {formatCount(segment.priorSignups)} prior ·{" "}
                    {formatPercent(segment.activationRate)} activated
                  </p>
                </div>
                <span className="shrink-0 text-sm font-medium text-emerald-600 tabular-nums">
                  +{formatPercent(segment.growth)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </SignalsCard>
  );
}

/**
 * Merchants worth asking to refer: activated, healthy, and already transacting.
 * The build ranks them; the list is exportable as a cohort like any other slice.
 */
export function AdvocateCandidatesCard({
  businessIds,
  rows,
  onDrill,
}: {
  businessIds: string[];
  rows: MerchantSignalRow[];
  onDrill: (cohort: Cohort) => void;
}) {
  const byId = new Map(rows.map((row) => [row.businessId, row]));
  const bySegment = new Map<string, number>();
  for (const id of businessIds) {
    const row = byId.get(id);
    if (!row) continue;
    bySegment.set(row.segmentLabel, (bySegment.get(row.segmentLabel) ?? 0) + 1);
  }
  const top = [...bySegment.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <SignalsCard
      title="Advocate candidates"
      subtitle="Activated merchants worth asking to refer"
      action={
        businessIds.length > 0 ? (
          <button
            type="button"
            className="cursor-pointer text-xs text-blue-600 hover:underline"
            onClick={() =>
              onDrill({
                label: "Advocate candidates",
                filter: { businessIds },
              })
            }
          >
            View list →
          </button>
        ) : undefined
      }
    >
      {businessIds.length === 0 ? (
        <EmptyState message="No merchant meets the advocate bar yet" />
      ) : (
        <div className="px-4 py-4">
          <p className="flex items-baseline gap-2">
            <Megaphone className="h-4 w-4 shrink-0 self-center text-blue-500" />
            <span className="text-foreground text-2xl font-semibold">
              {formatCount(businessIds.length)}
            </span>
            <span className="text-muted-foreground text-sm">candidates</span>
          </p>
          {top.length > 0 && (
            <ul className="mt-3 space-y-1">
              {top.map(([label, count]) => (
                <li
                  key={label}
                  className="text-muted-foreground flex justify-between text-xs"
                >
                  <span className="truncate">{label}</span>
                  <span className="tabular-nums">{formatCount(count)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SignalsCard>
  );
}
